import React, {FC, ReactNode, ReactElement} from 'react';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Skeleton, {SkeletonProps} from '@material-ui/lab/Skeleton';
import {CardProps, CardContentProps, BoxProps, withStyles} from '@material-ui/core';
import {Tooltip} from '../common';
import {GuidelineIcon} from '@epic-ui/icons';

import {getDirectionStyles} from '../../utils/ThemeUtils';

const StyledCard = withStyles<string, undefined, CardProps>(theme => ({
    root: {
        minHeight: '140px',
        backgroundColor: theme.palette.background.paper
    }
}))(Card);

const StyledIconWrapper = withStyles<string, undefined, BoxProps>(theme => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.text.secondary,
        padding: theme.spacing(0, 1)
    }
}))(Box);

const StyledCardContent = withStyles<string, undefined, CardContentProps>(theme => ({
    root: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '100%',
        padding: theme.spacing(3, 4),

        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(5, 8)
        }
    }
}))(CardContent);

const StyledValueWrapper = withStyles<string, undefined, BoxProps>(theme => ({
    root: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2, 0),

        [theme.breakpoints.up('fmd')]: {
            padding: theme.spacing(0)
        }
    }
}))(Box);

const StyledIcon = withStyles<string, undefined, BoxProps>(theme => ({
    root: {
        margin: getDirectionStyles(theme.direction)(
            theme.spacing(0, 0, 0, 4),
            theme.spacing(0, 4, 0, 0)
        )
    }
}))(Box);

const ItemSkeleton = withStyles<string, undefined, SkeletonProps>(theme => ({
    root: {
        width: `calc(100% - ${theme.spacing(6)}px)`,
        height: `calc(100% - ${theme.spacing(6)}px)`,
        margin: theme.spacing(3),
        borderRadius: theme.shape.borderRadius
    }
}))(props => <Skeleton variant="rect" {...props} />);

export interface StatisticsCardProps {
    title: string;
    trend: ReactElement | number;
    isLoading: boolean;
    description?: string;
    value?: ReactElement | number;
    children?: ReactNode;
    tooltip?: JSX.Element | string;
    titleTooltip?: string;
}

const StatisticsCard: FC<StatisticsCardProps> = ({
    children,
    title,
    value,
    trend,
    description,
    tooltip,
    titleTooltip,
    isLoading
}: StatisticsCardProps) => (
    <Box mb="2px">
        <StyledCard>
            {isLoading ? (
                <ItemSkeleton />
            ) : (
                <StyledCardContent>
                    <Box display="flex" flexDirection="column">
                        <Box display="flex" color="textSecondary">
                            <Typography variant="body2" color="textSecondary">
                                {title}
                            </Typography>
                            {titleTooltip && (
                                <Tooltip placement="top" title={titleTooltip}>
                                    <StyledIconWrapper>
                                        <GuidelineIcon fontSize="small" color="inherit" />
                                    </StyledIconWrapper>
                                </Tooltip>
                            )}
                        </Box>
                        <StyledValueWrapper>{value}</StyledValueWrapper>
                        <Box display="flex">
                            <Typography variant="body2" color="textSecondary">
                                {trend}
                                {description}
                            </Typography>
                            {tooltip}
                        </Box>
                    </Box>
                    {children && <StyledIcon ml={4}>{children}</StyledIcon>}
                </StyledCardContent>
            )}
        </StyledCard>
    </Box>
);

export default StatisticsCard;
